import { HTTPError } from 'ky';
import React, { useState } from 'react';
import { Link as TUILink } from 'theme-ui';
import { ArrowRightIcon, Box, Button, CheckIcon, Input, Text } from '../../ui';
import { startResumeMagicFill } from 'utils/magic-fill/resume';

type Props = {
  autoFocus?: boolean;
  inputLabel?: string;
  buttonText?: string;
  resumeId: string;
  smallButton?: boolean;
  userId: string;
  location: 'setup' | 'editor';
};

export default function LinkedinImportForm({
  autoFocus,
  inputLabel = 'Your LinkedIn profile URL',
  buttonText = 'Import From LinkedIn',
  resumeId,
  userId,
  smallButton = false,
}: Props) {
  type Status = 'initial' | 'loading' | 'success' | 'error' | 'urlerror';
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<Status>('initial');

  async function handleSubmit() {
    setStatus('loading');
    try {
      await startResumeMagicFill({
        resumeId,
        userId,
        enrichArgs: { profile: url },
      });
    } catch (error) {
      // Special handling for invalid URLs
      if (error instanceof HTTPError && error.response.status === 400) {
        setStatus('urlerror');
      } else {
        setStatus('error');
      }
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Text mb={3}>{inputLabel}</Text>
        <TUILink
          href="https://www.linkedin.com/in/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'blue', textDecoration: 'none' }}
        >
          Open profile
        </TUILink>
      </Box>
      <Box
        sx={{
          height: status === 'success' ? 0 : 62,
          opacity: status === 'success' ? 0 : 1,
          transition: 'height 500ms',
          overflow: 'hidden',
        }}
      >
        <Input
          placeholder="https://www.linkedin.com/in/dana-andrews"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
          onKeyDown={(e) => {
            // Submit for enter keypress
            if (e.keyCode === 13) {
              e.stopPropagation();
              void handleSubmit();
            }
          }}
          autoFocus={autoFocus}
          disabled={status === 'loading'}
          mb={3}
          type="url"
          required
        />
      </Box>
      {status === 'urlerror' && (
        <Text sx={{ mb: 3 }}>
          Invalid URL. It should look like this:
          <br />
          https://www.linkedin.com/in/your-name
        </Text>
      )}
      {status === 'error' && (
        <Text>
          We wern't able to import your LinkedIn profile. Please double check
          the URL.
        </Text>
      )}
      <Button
        onClick={handleSubmit}
        disabled={url === '' || status === 'loading' || status === 'success'}
        sx={{
          justifyContent: 'space-between',
          transition: 'all 500ms',
          py: smallButton ? 2 : 4,
          svg: {
            transition: 'all 500ms',
            transform: 'translateX(-5px)',
          },
          ':hover svg': {
            transform:
              status === 'success' ? 'translateX(-5px)' : 'translateX(5px)',
          },
          ':disabled': {
            bg: status === 'success' ? 'blue20' : 'black30',
          },
        }}
      >
        {status === 'loading'
          ? 'Importing...'
          : status === 'success'
          ? 'Import complete'
          : buttonText}
        {status === 'success' ? (
          <CheckIcon color="transparent" />
        ) : (
          <ArrowRightIcon
            color={status === 'loading' ? 'transparent' : 'white'}
          />
        )}
      </Button>
    </Box>
  );
}
